<script>
	import { onMount } from 'svelte';
	import { campaignsAPI, channelsAPI, contactsAPI } from './api.js';
	import { toast } from './lib/toast.js';
	import Icon from './lib/Icon.svelte';

	let loading = $state(true);
	let campaigns = $state([]);
	let channels = $state([]);
	let allTags = $state([]);

	// Wizard
	let wizardOpen = $state(false);
	let step = $state(1);
	let saving = $state(false);
	let form = $state(null);
	let estimate = $state(null);
	let estimateTimer = null;
	let templates = $state({ items: [], manual_only: true });
	let scheduleMode = $state('now'); // now | later
	let scheduledAt = $state('');

	// Detail drawer
	let detail = $state(null);
	let recipients = $state({ items: [], total: 0, page: 1, per_page: 50 });
	let recipientFilter = $state('');

	const STATUS_LABELS = {
		draft: 'Borrador', scheduled: 'Programada', sending: 'Enviando',
		done: 'Completada', cancelled: 'Cancelada', failed: 'Fallida'
	};
	const STATUS_BADGE = {
		draft: 'badge', scheduled: 'badge-info', sending: 'badge-warning',
		done: 'badge-success', cancelled: 'badge', failed: 'badge-danger'
	};

	let waChannels = $derived(channels.filter(c =>
		['whatsapp', 'whatsapp_qr'].includes(c.channel_type)));
	let selectedChannel = $derived(channels.find(c => c.id === form?.channel_id) || null);
	let isCloudApi = $derived(selectedChannel?.channel_type === 'whatsapp');

	async function load() {
		loading = true;
		try {
			const res = await campaignsAPI.list();
			campaigns = res.items || [];
		} catch (e) {
			toast.error(e.message || 'Error cargando campañas');
		} finally {
			loading = false;
		}
	}

	async function loadAux() {
		try { channels = await channelsAPI.getAll(); } catch {}
		try {
			const res = await contactsAPI.getAll(1, '', 200);
			const tags = new Set();
			(res.contacts || []).forEach(c =>
				((c.custom_attributes || {}).tags || []).forEach(t => tags.add(t)));
			allTags = [...tags].sort();
		} catch {}
	}

	onMount(() => { load(); loadAux(); });

	function openWizard() {
		form = {
			name: '', channel_id: waChannels[0]?.id || null,
			message_type: 'text', body: '',
			template_name: '', template_language: 'es', template_components: null,
			audience: { tags: [], has_phone: true, created_after: '' }
		};
		step = 1;
		estimate = null;
		scheduleMode = 'now';
		scheduledAt = '';
		wizardOpen = true;
	}

	function toggleTag(tag) {
		const tags = form.audience.tags.includes(tag)
			? form.audience.tags.filter(t => t !== tag)
			: [...form.audience.tags, tag];
		form.audience = { ...form.audience, tags };
		refreshEstimate();
	}

	function refreshEstimate() {
		clearTimeout(estimateTimer);
		estimateTimer = setTimeout(async () => {
			try {
				const audience = { ...form.audience };
				if (!audience.created_after) delete audience.created_after;
				estimate = await campaignsAPI.estimate(audience);
			} catch (e) {
				estimate = null;
			}
		}, 300);
	}

	async function goToStep(n) {
		if (n === 2) refreshEstimate();
		if (n === 3 && isCloudApi) {
			form.message_type = 'template';
			try { templates = await campaignsAPI.templates(form.channel_id); } catch {}
		}
		if (n === 3 && !isCloudApi) form.message_type = 'text';
		step = n;
	}

	async function submitCampaign() {
		saving = true;
		try {
			const payload = { ...form };
			if (!payload.audience.created_after) {
				payload.audience = { ...payload.audience };
				delete payload.audience.created_after;
			}
			const created = await campaignsAPI.create(payload);
			if (created.warning) toast.warning(created.warning);
			const when = scheduleMode === 'later' && scheduledAt ? scheduledAt : null;
			await campaignsAPI.schedule(created.id, when);
			toast.success(when ? 'Campaña programada' : 'Campaña en cola de envío');
			wizardOpen = false;
			load();
		} catch (e) {
			toast.error(e.message || 'No se pudo programar la campaña');
		} finally {
			saving = false;
		}
	}

	async function cancelCampaign(c) {
		if (!confirm(`¿Cancelar la campaña "${c.name}"?`)) return;
		try {
			await campaignsAPI.cancel(c.id);
			toast.success('Campaña cancelada');
			load();
		} catch (e) { toast.error(e.message); }
	}

	async function removeCampaign(c) {
		if (!confirm(`¿Eliminar la campaña "${c.name}"?`)) return;
		try {
			await campaignsAPI.remove(c.id);
			load();
		} catch (e) { toast.error(e.message); }
	}

	async function openDetail(c) {
		detail = c;
		recipientFilter = '';
		await loadRecipients(1);
	}
	async function loadRecipients(page) {
		try {
			recipients = await campaignsAPI.recipients(detail.id, page, recipientFilter);
		} catch (e) { toast.error(e.message); }
	}

	function pickTemplate(t) {
		form.template_name = t.name;
		form.template_language = t.language || 'es';
	}

	function fmtUsd(v) {
		return `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
	}
	function fmtDateTime(iso) {
		if (!iso) return '—';
		try { return new Date(iso).toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
		catch { return iso; }
	}
	function funnelPct(c, key) {
		const total = c.total_recipients || 1;
		return Math.round(((c.funnel?.[key] || 0) / total) * 100);
	}
</script>

<div class="campaigns-view">
	<div class="view-head">
		<div>
			<h2>Campañas de WhatsApp</h2>
			<p class="muted">Envíos masivos a audiencias segmentadas por etiquetas.</p>
		</div>
		<button class="btn btn-primary" onclick={openWizard} disabled={!waChannels.length}>
			<Icon name="send" size={15} /> Nueva campaña
		</button>
	</div>

	{#if !waChannels.length && !loading}
		<div class="empty card">
			<Icon name="send" size={32} />
			<h3>Conecta un canal de WhatsApp</h3>
			<p>Las campañas necesitan un canal de WhatsApp (API oficial o QR) activo.</p>
		</div>
	{/if}

	{#if loading}
		<div class="loading"><div class="spinner"></div></div>
	{:else if campaigns.length}
		<table class="data-table">
			<thead>
				<tr><th>Campaña</th><th>Canal</th><th>Estado</th><th>Embudo</th><th>Programada</th><th></th></tr>
			</thead>
			<tbody>
				{#each campaigns as c (c.id)}
					<tr>
						<td>
							<button class="row-link" onclick={() => openDetail(c)}>
								<div class="c-name">{c.name}</div>
								<div class="muted-sm">{c.total_recipients} destinatarios
									{#if c.overage_count > 0} · {c.overage_count} excedentes ({fmtUsd(c.overage_cost_usd)}){/if}
								</div>
							</button>
						</td>
						<td class="muted">{c.channel_name}</td>
						<td><span class="badge {STATUS_BADGE[c.status] || 'badge'}">{STATUS_LABELS[c.status] || c.status}</span></td>
						<td>
							{#if c.total_recipients > 0}
								<div class="funnel-mini" title="Enviados / Entregados / Leídos / Fallidos">
									<div class="fm-bar fm-sent" style="width:{funnelPct(c, 'sent') + funnelPct(c, 'delivered') + funnelPct(c, 'read')}%"></div>
									<div class="fm-bar fm-read" style="width:{funnelPct(c, 'read')}%"></div>
									<div class="fm-bar fm-failed" style="width:{funnelPct(c, 'failed')}%"></div>
								</div>
								<div class="muted-sm">
									✓ {(c.funnel?.sent || 0) + (c.funnel?.delivered || 0) + (c.funnel?.read || 0)} ·
									👁 {c.funnel?.read || 0} · ✗ {c.funnel?.failed || 0}
								</div>
							{:else}
								<span class="muted-sm">—</span>
							{/if}
						</td>
						<td class="muted">{fmtDateTime(c.scheduled_at)}</td>
						<td class="actions">
							{#if ['scheduled', 'sending'].includes(c.status)}
								<button class="icon-btn warn" title="Cancelar" onclick={() => cancelCampaign(c)}>
									<Icon name="x" size={13} />
								</button>
							{/if}
							{#if ['draft', 'done', 'cancelled', 'failed'].includes(c.status)}
								<button class="icon-btn danger" title="Eliminar" onclick={() => removeCampaign(c)}>
									<Icon name="trash" size={13} />
								</button>
							{/if}
						</td>
					</tr>
				{/each}
			</tbody>
		</table>
	{:else}
		<div class="empty card">
			<Icon name="send" size={32} />
			<h3>Sin campañas todavía</h3>
			<p>Crea tu primera campaña: recordatorios, promociones o reactivación de clientes.</p>
		</div>
	{/if}
</div>

<!-- ===== Wizard ===== -->
{#if wizardOpen}
	<div class="modal-back" onclick={() => (wizardOpen = false)} role="presentation">
		<div class="modal" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<h3>Nueva campaña</h3>
				<div class="steps">
					{#each ['Canal', 'Audiencia', 'Mensaje', 'Enviar'] as label, i}
						<span class="step-chip" class:active={step === i + 1} class:done={step > i + 1}>{i + 1}. {label}</span>
					{/each}
				</div>
				<button class="icon-btn" onclick={() => (wizardOpen = false)}><Icon name="x" size={16} /></button>
			</div>

			<div class="modal-body">
				{#if step === 1}
					<label>Nombre de la campaña *
						<input type="text" bind:value={form.name} placeholder="Promo fin de semana" />
					</label>
					<label>Canal de WhatsApp</label>
					<div class="channel-options">
						{#each waChannels as ch (ch.id)}
							<button class="channel-opt" class:selected={form.channel_id === ch.id}
								onclick={() => (form.channel_id = ch.id)}>
								<b>{ch.name}</b>
								<span class="muted-sm">{ch.channel_type === 'whatsapp' ? 'API oficial (plantillas)' : 'WhatsApp QR'}</span>
							</button>
						{/each}
					</div>
					{#if selectedChannel?.channel_type === 'whatsapp_qr'}
						<div class="warn-box">
							⚠️ Este canal usa un número personal: enviar muchos mensajes puede provocar que WhatsApp lo banee. Para volúmenes altos usa la API oficial.
						</div>
					{/if}

				{:else if step === 2}
					<label>Etiquetas (los contactos con cualquiera de estas)</label>
					{#if allTags.length}
						<div class="tag-row">
							{#each allTags as tag}
								<button class="tag-pill" class:on={form.audience.tags.includes(tag)}
									onclick={() => toggleTag(tag)}>{tag}</button>
							{/each}
						</div>
					{:else}
						<p class="muted-sm">No hay etiquetas aún — sin filtro se incluye a todos los contactos con teléfono. Puedes etiquetar contactos desde la vista Contactos.</p>
					{/if}
					<label style="margin-top:12px">Solo contactos creados después de
						<input type="date" bind:value={form.audience.created_after} onchange={refreshEstimate} />
					</label>
					{#if estimate}
						<div class="estimate-box">
							<b>{estimate.recipients}</b> destinatarios con teléfono
							{#if estimate.quota_limit > 0}
								· {estimate.in_quota} dentro de tu plan
								{#if estimate.overage > 0} · {estimate.overage} excedentes ≈ {fmtUsd(estimate.overage_cost_usd)}{/if}
							{/if}
						</div>
					{/if}

				{:else if step === 3}
					{#if isCloudApi}
						{#if templates.items.length}
							<label>Plantilla aprobada por Meta</label>
							<div class="template-list">
								{#each templates.items as t}
									<button class="channel-opt" class:selected={form.template_name === t.name}
										onclick={() => pickTemplate(t)}>
										<b>{t.name}</b>
										<span class="muted-sm">{t.language} · {t.category}</span>
									</button>
								{/each}
							</div>
						{:else}
							<p class="muted-sm">No se pudieron listar tus plantillas (agrega el <code>waba_id</code> en las credenciales del canal). Escribe el nombre exacto de una plantilla aprobada:</p>
							<label>Nombre de la plantilla *
								<input type="text" bind:value={form.template_name} placeholder="recordatorio_cita" />
							</label>
							<label>Idioma
								<input type="text" bind:value={form.template_language} placeholder="es" />
							</label>
						{/if}
					{:else}
						<label>Mensaje *
							<textarea rows="5" bind:value={form.body}
								placeholder={'Hola {{name}}, tenemos una promo especial para ti...'}></textarea>
						</label>
						<p class="muted-sm">Usa <code>{'{{name}}'}</code> para personalizar con el nombre del contacto.</p>
					{/if}

				{:else if step === 4}
					<label>¿Cuándo enviar?</label>
					<div class="channel-options">
						<button class="channel-opt" class:selected={scheduleMode === 'now'} onclick={() => (scheduleMode = 'now')}>
							<b>Ahora</b><span class="muted-sm">Sale en el próximo minuto</span>
						</button>
						<button class="channel-opt" class:selected={scheduleMode === 'later'} onclick={() => (scheduleMode = 'later')}>
							<b>Programar</b><span class="muted-sm">Elige fecha y hora</span>
						</button>
					</div>
					{#if scheduleMode === 'later'}
						<label style="margin-top:10px">Fecha y hora
							<input type="datetime-local" bind:value={scheduledAt} />
						</label>
					{/if}
					{#if estimate}
						<div class="estimate-box big">
							<div><b>{estimate.recipients}</b> mensajes a enviar</div>
							{#if estimate.quota_limit > 0}
								<div>{estimate.in_quota} incluidos en tu plan</div>
							{/if}
							{#if estimate.overage > 0}
								<div>{estimate.overage} excedentes ≈ <b>{fmtUsd(estimate.overage_cost_usd)}</b> (saldo: {fmtUsd(estimate.balance_usd)})</div>
								{#if !estimate.sufficient_credit}
									<div class="error-text">Saldo insuficiente — recarga créditos o reduce la audiencia.</div>
								{/if}
							{/if}
						</div>
					{/if}
				{/if}
			</div>

			<div class="modal-foot">
				{#if step > 1}
					<button class="btn btn-secondary" onclick={() => goToStep(step - 1)}>← Atrás</button>
				{/if}
				<div style="flex:1"></div>
				{#if step < 4}
					<button class="btn btn-primary" onclick={() => goToStep(step + 1)}
						disabled={(step === 1 && (!form.name.trim() || !form.channel_id)) ||
							(step === 3 && (isCloudApi ? !form.template_name.trim() : !form.body.trim()))}>
						Siguiente →
					</button>
				{:else}
					<button class="btn btn-primary" onclick={submitCampaign}
						disabled={saving || (estimate && !estimate.sufficient_credit) || (scheduleMode === 'later' && !scheduledAt)}>
						{saving ? 'Programando...' : scheduleMode === 'now' ? 'Enviar campaña' : 'Programar campaña'}
					</button>
				{/if}
			</div>
		</div>
	</div>
{/if}

<!-- ===== Detail drawer ===== -->
{#if detail}
	<div class="modal-back" onclick={() => (detail = null)} role="presentation">
		<div class="modal" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<h3>{detail.name}</h3>
				<button class="icon-btn" onclick={() => (detail = null)}><Icon name="x" size={16} /></button>
			</div>
			<div class="modal-body">
				<div class="filters">
					<select bind:value={recipientFilter} onchange={() => loadRecipients(1)}>
						<option value="">Todos los estados</option>
						<option value="pending">Pendientes</option>
						<option value="sent">Enviados</option>
						<option value="delivered">Entregados</option>
						<option value="read">Leídos</option>
						<option value="failed">Fallidos</option>
					</select>
					<span class="muted-sm">{recipients.total} destinatarios</span>
				</div>
				<table class="data-table">
					<thead><tr><th>Contacto</th><th>Teléfono</th><th>Estado</th><th>Error</th></tr></thead>
					<tbody>
						{#each recipients.items as r (r.id)}
							<tr>
								<td>{r.contact_name || '—'}</td>
								<td class="muted">{r.phone}</td>
								<td><span class="badge {r.status === 'failed' ? 'badge-danger' : r.status === 'read' ? 'badge-success' : r.status === 'pending' ? 'badge' : 'badge-info'}">{r.status}</span></td>
								<td class="muted-sm">{r.error || '—'}</td>
							</tr>
						{/each}
					</tbody>
				</table>
				{#if recipients.total > recipients.per_page}
					<div class="pager">
						<button class="btn btn-ghost btn-sm" disabled={recipients.page <= 1}
							onclick={() => loadRecipients(recipients.page - 1)}>← Anterior</button>
						<span class="muted-sm">Página {recipients.page}</span>
						<button class="btn btn-ghost btn-sm"
							disabled={recipients.page * recipients.per_page >= recipients.total}
							onclick={() => loadRecipients(recipients.page + 1)}>Siguiente →</button>
					</div>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	.campaigns-view { padding: 20px 24px; max-width: 1100px; margin: 0 auto; }
	.view-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 18px; }
	.view-head h2 { font-size: 20px; }
	.muted { color: var(--text-3); font-size: 13px; }
	.muted-sm { color: var(--text-3); font-size: 11.5px; }

	.loading { display: flex; justify-content: center; padding: 60px; }
	.spinner { width: 32px; height: 32px; border: 3px solid var(--border-1); border-top-color: var(--accent); border-radius: 50%; animation: spin 700ms linear infinite; }
	@keyframes spin { to { transform: rotate(360deg); } }
	.empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 48px; text-align: center; color: var(--text-3); }
	.empty h3 { color: var(--text-1); }

	.data-table { width: 100%; border-collapse: collapse; background: var(--surface-0); border: 1px solid var(--border-1); border-radius: 10px; overflow: hidden; font-size: 13px; }
	.data-table th { background: var(--surface-1); text-align: left; padding: 10px 14px; font-size: 11.5px; font-weight: 700; color: var(--text-3); text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid var(--border-1); }
	.data-table td { padding: 12px 14px; border-bottom: 1px solid var(--border-1); vertical-align: middle; }
	.data-table tr:last-child td { border-bottom: none; }
	.row-link { text-align: left; }
	.c-name { font-weight: 600; color: var(--text-1); }
	.actions { display: flex; gap: 4px; justify-content: flex-end; }
	.icon-btn { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border-radius: 6px; color: var(--text-3); }
	.icon-btn:hover { background: var(--surface-2); color: var(--text-1); }
	.icon-btn.warn:hover { background: var(--warning-soft); color: #b45309; }
	.icon-btn.danger:hover { background: var(--danger-soft); color: var(--danger); }

	.funnel-mini { position: relative; height: 6px; width: 140px; background: var(--surface-2); border-radius: 3px; overflow: hidden; margin-bottom: 3px; }
	.fm-bar { position: absolute; top: 0; left: 0; height: 100%; border-radius: 3px; }
	.fm-sent { background: var(--info); }
	.fm-read { background: var(--success); }
	.fm-failed { background: var(--danger); right: 0; left: auto; }

	/* Modal / wizard */
	.modal-back { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
	.modal { background: var(--surface-0); border-radius: 14px; box-shadow: var(--shadow-xl); width: 100%; max-width: 620px; max-height: 90vh; display: flex; flex-direction: column; }
	.modal-head { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid var(--border-1); }
	.modal-head h3 { font-size: 15px; white-space: nowrap; }
	.modal-body { padding: 16px 18px; overflow-y: auto; display: flex; flex-direction: column; gap: 10px; }
	.modal-foot { display: flex; gap: 8px; padding: 12px 18px; border-top: 1px solid var(--border-1); }
	.steps { display: flex; gap: 6px; flex: 1; flex-wrap: wrap; }
	.step-chip { font-size: 11px; font-weight: 600; color: var(--text-3); padding: 3px 9px; border-radius: 9999px; background: var(--surface-2); }
	.step-chip.active { background: var(--accent); color: #fff; }
	.step-chip.done { background: var(--accent-soft); color: var(--accent); }

	.channel-options, .template-list { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
	.template-list { max-height: 220px; overflow-y: auto; }
	.channel-opt { display: flex; flex-direction: column; gap: 2px; align-items: flex-start; padding: 12px; border: 1px solid var(--border-1); border-radius: 8px; text-align: left; transition: border-color var(--transition-fast); }
	.channel-opt:hover { border-color: var(--accent); }
	.channel-opt.selected { border-color: var(--accent); background: var(--accent-soft); }

	.warn-box { background: var(--warning-soft); color: #92400e; padding: 10px 12px; border-radius: 8px; font-size: 12.5px; line-height: 1.5; }
	.estimate-box { background: var(--accent-soft); color: var(--text-1); padding: 10px 12px; border-radius: 8px; font-size: 13px; }
	.estimate-box.big { display: flex; flex-direction: column; gap: 4px; }
	.error-text { color: var(--danger); font-weight: 600; }

	.tag-row { display: flex; flex-wrap: wrap; gap: 6px; }
	.tag-pill { padding: 4px 12px; border: 1px solid var(--border-2); border-radius: 9999px; font-size: 12.5px; color: var(--text-2); transition: all var(--transition-fast); }
	.tag-pill:hover { border-color: var(--accent); }
	.tag-pill.on { background: var(--accent); border-color: var(--accent); color: #fff; }

	.filters { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
	.filters select { width: auto; font-size: 13px; }
	.pager { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; }
</style>
